import { useCallback, useEffect, useMemo, useState } from 'react';

import { unix } from '../../../../../../utils/datetime';
import { useInterval } from '../../../../../../utils/timer';
import { useTimerContext } from '../../Context';
import { onActiveViewChanged } from '../../../../../../db/ppt';

const useStopWatch = () => {
  const { countdownStartTime, setCountdownStartTime, isTimerRunning, setIsTimerRunning } = useTimerContext();

  const [timerStartTime, setTimerStartTime] = useState(-1);
  const [displayTime, setDisplayTime] = useState(0);

  const { minutes1, minutes2, seconds1, seconds2 } = useMemo(() => {
    const minutes = `${Math.floor(displayTime / 60)}`.padStart(2, '0');
    const seconds = `${displayTime % 60}`.padStart(2, '0');
    return {
      minutes1: minutes[0],
      minutes2: minutes[1],
      seconds1: seconds[0],
      seconds2: seconds[1],
    };
  }, [displayTime]);

  const { start, stop } = useInterval(() => {
    const diff = timerStartTime - unix() + countdownStartTime;
    if (diff < 0) {
      handlePauseTimer();
      return;
    }

    setDisplayTime(diff);
  }, null);

  const handleStartTimer = () => {
    setTimerStartTime(unix());
    start(1000);
    setIsTimerRunning(true);
  };

  const handlePauseTimer = useCallback(() => {
    setCountdownStartTime(displayTime);
    stop();
    setIsTimerRunning(false);
  }, [stop, displayTime]);

  useEffect(
    function onMount() {
      setDisplayTime(countdownStartTime);
    },
    [countdownStartTime]
  );

  useEffect(
    function onUnmount() {
      return function cleanup() {
        setIsTimerRunning(false);
      };
    },
    [setIsTimerRunning]
  );

  useEffect(
    function onReturningToEditMode() {
      return function cleanup() {
        (async () => {
          await new Promise((resolve) => setTimeout(resolve, 0));
          onActiveViewChanged({
            onEditMode: () => {
              setCountdownStartTime(displayTime);
            },
          });
        })();
      };
    },
    [setCountdownStartTime, displayTime]
  );

  return {
    isTimerRunning,
    minutes1,
    minutes2,
    seconds1,
    seconds2,
    canStartTimer: displayTime > 0,
    handleStartTimer,
    handlePauseTimer,
  };
};

export default useStopWatch;
